import { AudioOutlined } from "@ant-design/icons";
import { CheckIcon, PlayIcon, StopIcon } from "@phosphor-icons/react";
import { useTranslate } from "#src/hooks";
import { Flex, Space, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import { useAntTheme } from "hooks/useAntTheme";
import { AudioSampleWave } from "pages/media/contacts/components/AudioSampleWave/";
import { VoiceWaveVisualizer } from "../components/VoiceWaveVisualizer";
import { StepHeader } from "./StepHeader";
import { ConsentText } from "./ConsentText";
import {
  MAX_SAMPLE_DURATION,
  MIN_TOTAL_DURATION,
  RECORD_COUNTDOWN_DURATION,
  RecordedSample,
  loadSound,
} from "../../hooks/useVoiceCloning";
import { nanoid } from "nanoid";
import { RecordControl } from "./controls/RecordControl";
import { VoiceCloneLanguageSelect } from "components/VoiceCloneLanguageSelect/";
import { VoiceCloneTextSample } from "../../components/VoiceCloneTextSample";
import { useVoiceLanguages } from "../../hooks/useVoiceLanguages";

export const VoiceStep = ({
  recordedSamples,
  setRecordedSamples,
  isCreating,
  onCreatingChange,
  selectedLanguage,
  setSelectedLanguage,
  clearAllSamples,
}: {
  recordedSamples: RecordedSample[];
  setRecordedSamples: React.Dispatch<React.SetStateAction<RecordedSample[]>>;
  isCreating: boolean;
  onCreatingChange?: (isCreating: boolean) => void;
  selectedLanguage: string | undefined;
  setSelectedLanguage: (value: string | undefined) => void;
  clearAllSamples: () => void;
}) => {
  const t = useTranslate();

  const { theme } = useAntTheme();
  const [recordCountdown, setRecordCountdown] = useState(
    RECORD_COUNTDOWN_DURATION
  );
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<RecordRTC | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingIntervalRef = useRef<NodeJS.Timeout>();
  // Hold the mic stream in a ref so the deferred startRecord (after the
  // countdown) can attach RecordRTC without re-calling getUserMedia. iOS
  // Safari requires getUserMedia to run inside the same user-activation
  // window as the tap; the 3s countdown burns through that window, so we
  // must request the stream synchronously on click.
  const streamRef = useRef<MediaStream | null>(null);

  const isRecordingRef = useRef(false);

  const startRecord = () => {
    const currentStream = streamRef.current;
    if (!currentStream) return;
    setRecording(true);
    setRecordingDuration(0);
    setStream(currentStream);
    const newMediaRecorder = new RecordRTC(currentStream, {
      recorderType: StereoAudioRecorder,
      mimeType: "audio/wav",
      bufferSize: 4096,
      numberOfAudioChannels: 1,
    });
    setMediaRecorder(newMediaRecorder);
    newMediaRecorder.startRecording();
  };

  useEffect(() => {
    if (!isCountingDown) return;

    const timeoutId = setTimeout(() => {
      if (recordCountdown <= 1) {
        setIsCountingDown(false);
        setRecordCountdown(RECORD_COUNTDOWN_DURATION);
        startRecord();
      } else {
        setRecordCountdown(recordCountdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isCountingDown, recordCountdown]);

  const handleStartRecording = () => {
    if (isCountingDown || !selectedLanguage) return;
    // Kick off the mic permission request synchronously inside the click
    // handler so iOS Safari accepts it as user-activated. We deliberately
    // do NOT await the Promise — the countdown should start immediately so
    // the user sees feedback, and the stream is consumed later in startRecord
    // via streamRef once the user grants permission.
    navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((newStream) => {
        streamRef.current = newStream;
      })
      .catch((error) => {
        console.error("Error requesting microphone permission:", error);
      });
    setIsCountingDown(true);
    setRecordCountdown(RECORD_COUNTDOWN_DURATION);
  };
  const stopRecord = useCallback(() => {
    (async () => {
      const blob = mediaRecorder?.getBlob();
      if (blob) {
        const audio = await loadSound(blob);
        setRecordedSamples((prev) => [...prev, { blob, audio }]);
      }

      for (const track of stream?.getTracks() ?? []) {
        track.stop();
      }
      setRecordingDuration(0);
    })().catch((error) => {
      console.error("Error while stopping recording:", error);
    });
  }, [mediaRecorder, stream, setRecordedSamples]);

  const handleStopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    setRecording(false);

    mediaRecorder?.stopRecording(stopRecord);
  }, [mediaRecorder, stopRecord]);

  const handleRecordingDuration = useCallback(() => {
    setRecordingDuration((prevTime) => {
      if (prevTime + 1 >= MAX_SAMPLE_DURATION) {
        clearInterval(recordingIntervalRef.current);
        // Stop recording when max duration is reached
        // Using setTimeout to defer the stopRecord call outside the setState callback
        setTimeout(() => handleStopRecording(), 0);
      }
      return prevTime + 1;
    });
  }, [handleStopRecording]);

  useEffect(() => {
    if (recording) {
      recordingIntervalRef.current = setInterval(handleRecordingDuration, 1000);
    }
    return () => clearInterval(recordingIntervalRef.current);
  }, [recording, handleRecordingDuration]);

  useEffect(() => {
    isRecordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    onCreatingChange?.(isCreating);
  }, [isCreating, onCreatingChange]);

  const handleMicButtonClick = () => {
    if (recording) {
      handleStopRecording();
    } else if (recordedSamples.length > 0 && !play) {
      setPlay(true);
    } else if (recordedSamples.length > 0 && play) {
      setPlay(false);
    } else if (selectedLanguage) {
      handleStartRecording();
    }
  };

  const [play, setPlay] = useState(false);

  const { data: languagesList, isLoading: isLoadingLanguages } =
    useVoiceLanguages();
  const textForRecording = selectedLanguage
    ? (languagesList?.[selectedLanguage]?.text_for_recording ?? null)
    : null;

  const stopDisabled = recording && recordingDuration <= MIN_TOTAL_DURATION;
  const isSamplePlaying = !recording && recordedSamples.length > 0 && play;
  const noSamplesRecorded = recordedSamples.length === 0;

  const recordControlIcon = useMemo(() => {
    if (isCountingDown && recordCountdown > 0) {
      return <span style={{ fontSize: 32 }}>{recordCountdown}</span>;
    }
    if (
      !recording &&
      recordCountdown !== 0 &&
      !isCountingDown &&
      noSamplesRecorded &&
      selectedLanguage
    ) {
      return <AudioOutlined style={{ fontSize: 32 }} />;
    }

    if (recording || isSamplePlaying) {
      return <StopIcon style={{ fontSize: 32 }} />;
    }

    if (!noSamplesRecorded && !play) {
      return <PlayIcon style={{ fontSize: 32 }} />;
    }

    return null;
  }, [
    isCountingDown,
    recordCountdown,
    recording,
    noSamplesRecorded,
    play,
    isSamplePlaying,
    selectedLanguage,
  ]);

  return (
    <>
      <Flex
        vertical
        gap={16}
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <StepHeader
          title={t("src.components.ContactCloningMobile.voice.title")}
          description={
            noSamplesRecorded ? (
              t("src.components.ContactCloningMobile.voice.description")
            ) : (
              <VoiceRecordedTag />
            )
          }
        />
        <Flex vertical gap={10} style={{ width: "100%" }}>
          {!selectedLanguage && (
            <Typography.Text>
              {t("media.components.VoiceProfileCreateForm.selectLanguage")}
            </Typography.Text>
          )}
          <VoiceCloneLanguageSelect
            value={selectedLanguage}
            onChange={(value) => {
              if (!value) clearAllSamples();
              setSelectedLanguage(value);
            }}
            languages={languagesList}
            loading={isLoadingLanguages}
            hasSamples={!noSamplesRecorded}
          />
        </Flex>
        {textForRecording && (
          <VoiceCloneTextSample textForRecording={textForRecording} />
        )}
        <ConsentText
          consentText={t(
            "src.components.ContactCloningMobile.voice.consentTextVoice"
          )}
          disabled={!noSamplesRecorded}
        />
        <div style={{ minHeight: 25 }}>
          {(stopDisabled || recording) && (
            <Typography.Text
              style={{ fontSize: 14 }}
            >{`${t("media.components.VoiceProfileCreateForm.minimum")} ${MIN_TOTAL_DURATION} ${t("media.components.VoiceProfileCreateForm.seconds")}`}</Typography.Text>
          )}
          {noSamplesRecorded && !recording && selectedLanguage && (
            <Typography.Text style={{ fontSize: 14 }}>
              {t("src.components.ContactCloningMobile.voice.tapTheButton")}
            </Typography.Text>
          )}
        </div>
        <div style={{ minHeight: 60 }}>
          {noSamplesRecorded || recording ? (
            <VoiceWaveVisualizer
              idle={!recording && noSamplesRecorded}
              stream={stream}
              barColor={theme.colorPrimary}
            />
          ) : (
            <Flex vertical>
              {recordedSamples.map((sample) => (
                <AudioSampleWave
                  key={sample.blob.size ?? nanoid()}
                  audio={sample.blob}
                  play={play}
                  setPlay={setPlay}
                />
              ))}
            </Flex>
          )}
        </div>
      </Flex>
      <div style={{ paddingTop: 12 }}>
        <RecordControl
          stopDisabled={stopDisabled}
          handleMicButtonClick={handleMicButtonClick}
          icon={recordControlIcon}
          showDuration={noSamplesRecorded}
          recordingDuration={recordingDuration}
        />
      </div>
    </>
  );
};

const VoiceRecordedTag = () => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  return (
    <Tag
      color={"#E6F3E6"}
      style={{
        borderRadius: 20,
        padding: "6px 12px",
      }}
    >
      <Space align="center">
        <CheckIcon color={theme.colorSuccess} size={16} />
        <Typography.Text style={{ color: theme.colorSuccess }}>
          {t("src.components.ContactCloningMobile.voice.voiceSampleRecorded")}
        </Typography.Text>
      </Space>
    </Tag>
  );
};
